// Purpose: define the Agent Spec v1 UI contract and local validation helpers.
export type EndpointingMode = 'server_vad' | 'stt_final' | 'hybrid';
export type FirstMessageMode = 'speak' | 'wait_for_caller';
export type AmbiencePreset = 'office' | 'restaurant' | 'lounge';
export type ToolKind = 'disposition_call' | 'live_transfer' | 'end_call' | 'capture_fields' | 'call_summary' | 'mcp_connector';
export type CaptureFieldType = 'string' | 'number' | 'integer' | 'boolean' | 'string_list';
export type EndCallFinalMessageMode = 'natural' | 'scripted' | 'silent';

export interface EndCallConfig {
  final_message_mode?: EndCallFinalMessageMode;
  final_message?: string;
}

export interface CaptureFieldDefinition {
  key: string;
  label: string;
  type: CaptureFieldType;
  description?: string;
  sensitive?: boolean;
}
export type SttProvider = 'deepgram' | 'openai' | 'custom';
export type LlmProvider = 'openai' | 'anthropic' | 'custom';
export type TtsProvider = 'elevenlabs' | 'openai' | 'cartesia' | 'google' | 'custom';

export interface FirstClauseBufferSettings {
  enabled: boolean;
  min_chars: number;
  context_chars: number;
}

export interface ChunkPlanSettings {
  enabled: boolean;
  minCharacters: number;
  punctuationBoundaries: string[];
}

export interface TtsSettings {
  language?: string | null;
  speed?: number | null;
  emotion?: string | string[] | null;
  volume?: number | null;
  sample_rate?: number | null;
  streaming_latency?: number | null;
  stability?: number | null;
  similarity_boost?: number | null;
  style?: number | null;
  use_speaker_boost?: boolean | null;
  instructions?: string | null;
  chunk_plan?: ChunkPlanSettings | null;
  first_clause_buffer?: FirstClauseBufferSettings | null;
}

export interface CallableAgentSpecV1 {
  schema_version: 'callable.agent.v1';
  name: string;
  description: string | null;
  conversation: {
    first_message: string;
    first_message_mode?: FirstMessageMode | null;
    system_prompt: string;
    endpointing: {
      mode: EndpointingMode;
      min_silence_ms: number;
      speech_final_timeout_ms: number;
    };
    ambience?: {
      enabled: boolean;
      preset: AmbiencePreset;
      volume_db: number;
    } | null;
    silence_timeout_seconds: number;
    max_call_duration_seconds: number;
    allow_interruptions: boolean;
    interruption_min_words?: number | null;
  };
  providers: {
    stt: { provider: SttProvider; model: string; language: string; settings?: Record<string, unknown> };
    llm: { provider: LlmProvider; model: string; temperature: number; settings?: Record<string, unknown> };
    tts: { provider: TtsProvider; model: string; voice: string; settings: TtsSettings };
  };
  server?: ServerCallbackSpec | null;
  context?: Record<string, unknown> | null;
  triggers?: Array<Record<string, unknown>> | null;
  tools: Array<Record<string, unknown> & { kind: ToolKind; name: string; connector_id?: string | null; config?: Record<string, unknown> }>;
  routing: { strategy: 'single_agent'; targets: string[] };
  recording: { record_audio: boolean; transcribe: boolean };
  limits: { max_tool_calls_per_call: number; max_llm_tokens_per_turn: number };
}

// Optional/defaulted fields mirror ServerCallbackSpec; the API remains authoritative
// for callback URL safety, credential ownership, and enabled dependencies.
export interface ServerCallbackSpec {
  enabled?: boolean;
  url?: string | null;
  messages?: Array<'status_update' | 'end_of_call_report' | 'tool_call' | 'transfer_destination_request'>;
  auth_mode?: 'hmac_sha256' | 'header_secret' | 'google_oidc';
  auth_header_name?: string;
  signing_secret_ref?: string | null;
  oidc_audience?: string | null;
  timeout_ms?: number;
  max_response_bytes?: number;
}

export interface ValidationResult {
  valid: boolean;
  messages: string[];
}

export interface AgentSpecDiffRow {
  path: string;
  before: string;
  after: string;
}

export function diffAgentSpecs(before: Record<string, unknown>, after: Record<string, unknown>): AgentSpecDiffRow[] {
  const beforeFlat = flattenJson(before);
  const afterFlat = flattenJson(after);
  const paths = Array.from(new Set([...beforeFlat.keys(), ...afterFlat.keys()])).sort();
  return paths
    .filter((path) => beforeFlat.get(path) !== afterFlat.get(path))
    .map((path) => ({ path, before: beforeFlat.get(path) ?? '∅', after: afterFlat.get(path) ?? '∅' }));
}

function flattenJson(value: unknown, prefix = '$', rows = new Map<string, string>()): Map<string, string> {
  if (Array.isArray(value)) {
    if (value.length === 0) rows.set(prefix, '[]');
    value.forEach((item, index) => flattenJson(item, `${prefix}[${index}]`, rows));
    return rows;
  }
  if (isRecord(value)) {
    const entries = Object.entries(value).sort(([left], [right]) => left.localeCompare(right));
    if (entries.length === 0) rows.set(prefix, '{}');
    entries.forEach(([key, item]) => flattenJson(item, `${prefix}.${key}`, rows));
    return rows;
  }
  rows.set(prefix, value === undefined ? 'undefined' : JSON.stringify(value));
  return rows;
}

const topLevelKeys = ['schema_version', 'name', 'description', 'conversation', 'providers', 'server', 'context', 'triggers', 'tools', 'routing', 'recording', 'limits'];
const conversationKeys = ['first_message', 'first_message_mode', 'system_prompt', 'endpointing', 'ambience', 'silence_timeout_seconds', 'max_call_duration_seconds', 'allow_interruptions', 'interruption_min_words'];
const endpointingKeys = ['mode', 'min_silence_ms', 'speech_final_timeout_ms'];
const ambienceKeys = ['enabled', 'preset', 'volume_db'];
const providerBlockKeys = ['stt', 'llm', 'tts'];
const sttKeys = ['provider', 'model', 'language', 'settings'];
const llmKeys = ['provider', 'model', 'temperature', 'settings'];
const ttsKeys = ['provider', 'model', 'voice', 'settings'];
const ttsSettingsKeys = ['language', 'speed', 'emotion', 'volume', 'sample_rate', 'streaming_latency', 'stability', 'similarity_boost', 'style', 'use_speaker_boost', 'instructions', 'chunk_plan', 'first_clause_buffer'];
const chunkPlanKeys = ['enabled', 'minCharacters', 'punctuationBoundaries'];
const firstClauseBufferKeys = ['enabled', 'min_chars', 'context_chars'];
const toolKeys = ['kind', 'name', 'connector_id', 'description', 'input_schema', 'output_schema', 'required_context', 'required_credentials', 'timeout_ms', 'max_attempts', 'audit', 'config'];
const contextKeys = ['inputs', 'collections', 'writebacks', 'rendering', 'sample_context'];
const triggerKeys = ['kind', 'name', 'description', 'context'];
const captureFieldKeys = ['key', 'label', 'type', 'description', 'sensitive'];
const captureFieldTypes: CaptureFieldType[] = ['string', 'number', 'integer', 'boolean', 'string_list'];
const endCallConfigKeys = ['final_message_mode', 'final_message'];
const endCallFinalMessageModes: EndCallFinalMessageMode[] = ['natural', 'scripted', 'silent'];

export const supportedProviderModels = {
  stt: { deepgram: ['flux-general-en', 'flux-general-multi', 'nova-3'] },
  llm: { openai: ['gpt-4o-mini'] },
  tts: {
    elevenlabs: ['eleven_flash_v2_5'],
    cartesia: ['sonic-3.6', 'sonic-3', 'sonic-2'],
    google: ['gemini-3.8-flash-tts', 'gemini-3.8-flash-lite-tts'],
  },
} as const;
export const supportedSttLanguages = ['en', 'en-AU'] as const;
export const supportedLlmMaxTemperature = 0.7;

export function defaultTtsModelForProvider(provider: TtsProvider): string {
  const models = supportedProviderModels.tts[provider as keyof typeof supportedProviderModels.tts];
  if (!models?.length) throw new Error(`No supported TTS models configured for provider: ${provider}`);
  return models[0];
}

// Mirrors GEMINI_TTS_VOICES in apps/api/src/callable_console_api/model_allowlist.py.
export const geminiTtsVoices = ['Zephyr', 'Puck', 'Charon', 'Kore', 'Fenrir', 'Leda', 'Orus', 'Aoede', 'Callirrhoe', 'Autonoe', 'Enceladus', 'Iapetus', 'Umbriel', 'Algieba', 'Despina', 'Erinome', 'Algenib', 'Rasalgethi', 'Laomedeia', 'Achernar', 'Alnilam', 'Schedar', 'Gacrux', 'Pulcherrima', 'Achird', 'Zubenelgenubi', 'Vindemiatrix', 'Sadachbia', 'Sadaltager', 'Sulafat'] as const;

// Mirrors require_supported_tts_voice in model_allowlist.py: any Gemini voice ID or name is
// accepted (custom voices are `voice_...`), except a built-in name in the wrong case and a
// `voicekey_...` value, which is a secret key to a cloned voice.
function geminiVoiceMessage(voice: string, messages: string[]) {
  if ((geminiTtsVoices as readonly string[]).includes(voice)) return;
  if (voice.toLowerCase().startsWith('voicekey_')) {
    messages.push('providers.tts.voice must not be a voicekey_ value; save the voice in Google and use its voice_ ID.');
    return;
  }
  const builtIn = geminiTtsVoices.find((name) => name.toLowerCase() === voice.toLowerCase());
  if (builtIn) messages.push(`providers.tts.voice must be written ${builtIn}, not ${voice}.`);
  else if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,159}$/.test(voice)) messages.push(`providers.tts.voice is not a Gemini voice name or voice ID: ${voice}.`);
}

// Voice IDs belong to one provider, so switching provider must not keep the old one: an
// ElevenLabs voice ID sent to Cartesia fails the call. Gemini has named voices to default to.
export function defaultTtsVoiceForProvider(provider: TtsProvider): string {
  return provider === 'google' ? 'Kore' : '';
}

export function ttsVoiceSuggestionsForProvider(provider: TtsProvider): string[] {
  return provider === 'google' ? [...geminiTtsVoices] : [];
}

export function supportedTtsModelsForProvider(provider: TtsProvider): string[] {
  const models = supportedProviderModels.tts[provider as keyof typeof supportedProviderModels.tts];
  if (!models?.length) return [];
  return [...models];
}

export const defaultAgentSpec: CallableAgentSpecV1 = {
  schema_version: 'callable.agent.v1',
  name: 'Support triage',
  description: 'Answers inbound support calls and collects caller intent.',
  conversation: {
    first_message: 'Hi, this is CallableAI. How can I help?',
    first_message_mode: 'speak',
    system_prompt: 'You are a concise and helpful phone receptionist for CallableAI.',
    endpointing: { mode: 'hybrid', min_silence_ms: 600, speech_final_timeout_ms: 1200 },
    ambience: { enabled: false, preset: 'office', volume_db: -18 },
    silence_timeout_seconds: 30,
    max_call_duration_seconds: 600,
    allow_interruptions: true,
    interruption_min_words: 3,
  },
  providers: {
    stt: { provider: 'deepgram', model: 'flux-general-en', language: 'en-AU' },
    llm: { provider: 'openai', model: 'gpt-4o-mini', temperature: 0.4 },
    tts: { provider: 'elevenlabs', model: 'eleven_flash_v2_5', voice: 'rachel', settings: { first_clause_buffer: { enabled: true, min_chars: 20, context_chars: 10 } } },
  },
  // Memory only reaches the agent if the spec declares these inputs. A default of null
  // meant every new agent silently had no memory at all, and the fields the platform
  // populates were dropped by context preflight before the prompt was built.
  // omit: a contact with no history simply has no memory, which is not a failure.
  context: {
    inputs: [
      { name: 'contact.memory_summary', type: 'string', source: 'contact', required: false, redact: false, missing_value_policy: 'omit' },
      { name: 'contact.known_preferences', type: 'string', source: 'contact', required: false, redact: false, missing_value_policy: 'omit' },
      { name: 'contact.open_items', type: 'string', source: 'contact', required: false, redact: false, missing_value_policy: 'omit' },
      { name: 'contact.last_interaction_summary', type: 'string', source: 'contact', required: false, redact: false, missing_value_policy: 'omit' },
      { name: 'contact.last_interaction_at', type: 'string', source: 'contact', required: false, redact: false, missing_value_policy: 'omit' },
      { name: 'contact.last_interaction_type', type: 'string', source: 'contact', required: false, redact: false, missing_value_policy: 'omit' },
      { name: 'contact.last_interaction_by', type: 'string', source: 'contact', required: false, redact: false, missing_value_policy: 'omit' },
    ],
  },
  triggers: null,
  // Memory and disposition are always-on platform capabilities: every call needs a
  // durable record and an outcome. They are declared here so new agents get them without
  // configuration, and remain removable by the tenant.
  tools: [
    {
      kind: 'call_summary',
      name: 'Call summary',
      description: 'Record what this call achieved for durable contact memory.',
      config: {},
    },
    {
      kind: 'disposition_call',
      name: 'Call disposition',
      description: 'Record the business outcome of this call.',
      config: {
        available_dispositions: [
          { key: 'booked', label: 'Booked', description: 'A concrete appointment or order was agreed.' },
          { key: 'callback_requested', label: 'Callback requested', description: 'The caller asked to be contacted again later.' },
          { key: 'information_provided', label: 'Information provided', description: 'The caller got the information they wanted; nothing further agreed.' },
          { key: 'not_interested', label: 'Not interested', description: 'The caller declined or is not a fit.' },
          { key: 'voicemail', label: 'Voicemail', description: 'The assistant heard explicit voicemail or answering-machine evidence, such as a greeting, tone, or request to leave a message.' },
        ],
      },
    },
  ],
  routing: { strategy: 'single_agent', targets: [] },
  recording: { record_audio: true, transcribe: true },
  // Must cover the declared tools: the runtime fails closed when the limit is lower than
  // the number of declared native tools, which would break every call.
  limits: { max_tool_calls_per_call: 4, max_llm_tokens_per_turn: 4096 },
};

export function formatAgentSpec(spec: CallableAgentSpecV1): string {
  return JSON.stringify(spec, null, 2);
}

export function parseAgentSpec(jsonText: string): unknown {
  return JSON.parse(jsonText) as unknown;
}

export function coerceValidAgentSpec(value: unknown): CallableAgentSpecV1 {
  const validation = validateAgentSpec(value, { runtimeSupport: false });
  if (!validation.valid) {
    throw new Error(validation.messages.join(' '));
  }
  return normalizeAgentSpec(value);
}

export function getCaptureFields(spec: CallableAgentSpecV1): CaptureFieldDefinition[] {
  const tool = spec.tools.find((item) => item.kind === 'capture_fields');
  const fields = isRecord(tool?.config) ? tool.config.fields : undefined;
  if (!Array.isArray(fields)) return [];
  return fields.filter(isCaptureFieldDefinition).map((field) => ({ ...field }));
}

export function getEndCallConfig(spec: CallableAgentSpecV1): EndCallConfig | null {
  const tool = spec.tools.find((item) => item.kind === 'end_call');
  if (!tool) return null;
  const config = isRecord(tool.config) ? tool.config : {};
  const mode = endCallFinalMessageModes.includes(config.final_message_mode as EndCallFinalMessageMode)
    ? config.final_message_mode as EndCallFinalMessageMode
    : 'natural';
  const finalMessage = typeof config.final_message === 'string' ? config.final_message : '';
  return { final_message_mode: mode, final_message: finalMessage };
}

export function setEndCallConfig(spec: CallableAgentSpecV1, config: EndCallConfig | null): CallableAgentSpecV1 {
  const otherTools = spec.tools.filter((tool) => tool.kind !== 'end_call');
  if (config === null) {
    return {
      ...spec,
      tools: otherTools,
      limits: {
        ...spec.limits,
        max_tool_calls_per_call: otherTools.length === 0 ? 0 : Math.max(spec.limits.max_tool_calls_per_call, otherTools.length),
      },
    };
  }
  const mode = config.final_message_mode ?? 'natural';
  const endCallConfig: Record<string, unknown> = {};
  if (mode !== 'natural') endCallConfig.final_message_mode = mode;
  if (mode === 'scripted') endCallConfig.final_message = config.final_message ?? '';
  const tools = [...otherTools, {
    kind: 'end_call' as const,
    name: 'End call',
    description: 'Let the agent hang up cleanly when the conversation is complete.',
    config: endCallConfig,
  }];
  return {
    ...spec,
    tools,
    limits: {
      ...spec.limits,
      max_tool_calls_per_call: Math.max(spec.limits.max_tool_calls_per_call, tools.length),
    },
  };
}

export function setCaptureFields(spec: CallableAgentSpecV1, fields: CaptureFieldDefinition[]): CallableAgentSpecV1 {
  const otherTools = spec.tools.filter((tool) => tool.kind !== 'capture_fields');
  if (fields.length === 0) {
    return {
      ...spec,
      tools: otherTools,
      limits: {
        ...spec.limits,
        max_tool_calls_per_call: otherTools.length === 0 ? 0 : Math.max(spec.limits.max_tool_calls_per_call, otherTools.length),
      },
    };
  }
  const captureTool = {
    kind: 'capture_fields' as const,
    name: 'Capture fields',
    description: 'Silently capture safe structured call details as the caller provides them.',
    config: { fields: fields.map((field) => ({ ...field })) },
  };
  const tools = [...otherTools, captureTool];
  return {
    ...spec,
    tools,
    limits: {
      ...spec.limits,
      max_tool_calls_per_call: Math.max(spec.limits.max_tool_calls_per_call, tools.length),
    },
  };
}

// Turn-taking values a Vapi-compatible import stores in providers.stt.settings. The runtime
// reads these before the conversation fields this builder edits, so when a key is present,
// even as null, the visible field has no effect (real_ai_config._runtime_stt_settings).
const turnTakingOverrideFields = [
  { key: 'endpointing_min_delay', field: 'Min silence', unit: 'seconds' },
  { key: 'endpointing_max_delay', field: 'Speech final timeout', unit: 'seconds' },
  { key: 'interruption_min_words', field: 'Interruption min words', unit: 'count' },
] as const;

export type TurnTakingOverride = { key: string; field: string; display: string };

export function turnTakingOverrides(spec: CallableAgentSpecV1): TurnTakingOverride[] {
  const settings = spec.providers.stt.settings;
  if (!isRecord(settings)) return [];
  return turnTakingOverrideFields.flatMap(({ key, field, unit }) => {
    if (!Object.hasOwn(settings, key)) return [];
    const value = settings[key];
    // The runtime rejects a present-but-null value, so the call fails before it is answered.
    const display = value === null ? 'null (calls fail to start)' : unit === 'seconds' && isNumber(value) ? `${Math.round(value * 1000)} ms` : String(value);
    return [{ key, field, display }];
  });
}

export function clearTurnTakingOverrides(spec: CallableAgentSpecV1): CallableAgentSpecV1 {
  const settings = spec.providers.stt.settings;
  if (!isRecord(settings)) return spec;
  const remaining = Object.fromEntries(Object.entries(settings).filter(([key]) => !turnTakingOverrideFields.some((item) => item.key === key)));
  return { ...spec, providers: { ...spec.providers, stt: { ...spec.providers.stt, settings: remaining } } };
}

export function preemptiveTtsEnabled(spec: CallableAgentSpecV1): boolean {
  const settings = spec.providers.stt.settings;
  return !isRecord(settings) || settings.preemptive_tts !== false;
}

export function setPreemptiveTtsEnabled(spec: CallableAgentSpecV1, enabled: boolean): CallableAgentSpecV1 {
  const settings = isRecord(spec.providers.stt.settings) ? { ...spec.providers.stt.settings } : {};
  settings.preemptive_tts = enabled;
  return { ...spec, providers: { ...spec.providers, stt: { ...spec.providers.stt, settings } } };
}

export function fieldKeyFromLabel(label: string): string {
  const key = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  if (/^[a-z]/.test(key)) return key.slice(0, 80);
  return key ? `field_${key}`.slice(0, 80) : 'captured_field';
}

function isCaptureFieldDefinition(value: unknown): value is CaptureFieldDefinition {
  if (!isRecord(value)) return false;
  return typeof value.key === 'string' && typeof value.label === 'string' && typeof value.type === 'string' && captureFieldTypes.includes(value.type as CaptureFieldType);
}

// `runtimeSupport: false` checks only the saved Agent Spec shape. Loading uses it so an agent
// saved with a model the runtime does not run yet (a Vapi import, an older draft) still opens
// in the builder, where the full check lists what to change before it can be saved.
export function validateAgentSpec(value: unknown, options: { runtimeSupport?: boolean } = {}): ValidationResult {
  const runtimeSupport = options.runtimeSupport ?? true;
  const messages: string[] = [];
  if (!isRecord(value)) return { valid: false, messages: ['Spec must be a JSON object.'] };

  rejectUnknownKeys(value, topLevelKeys, '', messages);
  literal(value.schema_version, 'callable.agent.v1', 'schema_version', messages);
  boundedString(value.name, 'name', 1, 120, messages);
  if (value.description !== null && value.description !== undefined) boundedString(value.description, 'description', 0, 1000, messages);

  const conversation = requiredRecord(value.conversation, 'conversation', messages);
  if (conversation) {
    rejectUnknownKeys(conversation, conversationKeys, 'conversation', messages);
    boundedString(conversation.first_message, 'conversation.first_message', 0, 500, messages);
    if (conversation.first_message_mode !== undefined && conversation.first_message_mode !== null) oneOf(conversation.first_message_mode, ['speak', 'wait_for_caller'], 'conversation.first_message_mode', messages);
    const firstMessageMode = conversation.first_message_mode ?? 'speak';
    if (firstMessageMode === 'speak' && conversation.first_message === '') messages.push('conversation.first_message is required when first_message_mode is speak.');
    boundedString(conversation.system_prompt, 'conversation.system_prompt', 1, 16000, messages);
    range(conversation.silence_timeout_seconds, 'conversation.silence_timeout_seconds', 5, 300, messages);
    range(conversation.max_call_duration_seconds, 'conversation.max_call_duration_seconds', 30, 7200, messages);
    if (typeof conversation.allow_interruptions !== 'boolean') messages.push('conversation.allow_interruptions must be boolean.');
    if (conversation.interruption_min_words !== undefined && conversation.interruption_min_words !== null) range(conversation.interruption_min_words, 'conversation.interruption_min_words', 1, 10, messages);
    const endpointing = requiredRecord(conversation.endpointing, 'conversation.endpointing', messages);
    if (endpointing) {
      rejectUnknownKeys(endpointing, endpointingKeys, 'conversation.endpointing', messages);
      oneOf(endpointing.mode, ['server_vad', 'stt_final', 'hybrid'], 'conversation.endpointing.mode', messages);
      range(endpointing.min_silence_ms, 'conversation.endpointing.min_silence_ms', 100, 5000, messages);
      range(endpointing.speech_final_timeout_ms, 'conversation.endpointing.speech_final_timeout_ms', 100, 10000, messages);
      if (isNumber(endpointing.speech_final_timeout_ms) && isNumber(endpointing.min_silence_ms) && endpointing.speech_final_timeout_ms < endpointing.min_silence_ms) {
        messages.push('conversation.endpointing.speech_final_timeout_ms must be >= min_silence_ms.');
      }
    }
    if (conversation.ambience !== undefined && conversation.ambience !== null) {
      const ambience = requiredRecord(conversation.ambience, 'conversation.ambience', messages);
      if (ambience) {
        rejectUnknownKeys(ambience, ambienceKeys, 'conversation.ambience', messages);
        if (typeof ambience.enabled !== 'boolean') messages.push('conversation.ambience.enabled must be boolean.');
        oneOf(ambience.preset, ['office', 'restaurant', 'lounge'], 'conversation.ambience.preset', messages);
        range(ambience.volume_db, 'conversation.ambience.volume_db', -45, -6, messages);
      }
    }
  }

  const providers = requiredRecord(value.providers, 'providers', messages);
  if (providers) {
    rejectUnknownKeys(providers, providerBlockKeys, 'providers', messages);
    validateProvider(providers.stt, 'providers.stt', ['deepgram', 'openai', 'custom'], sttKeys, messages);
    validateProvider(providers.llm, 'providers.llm', ['openai', 'anthropic', 'custom'], llmKeys, messages);
    validateProvider(providers.tts, 'providers.tts', ['elevenlabs', 'openai', 'cartesia', 'google', 'custom'], ttsKeys, messages, runtimeSupport);
    const stt = isRecord(providers.stt) ? providers.stt : undefined;
    const llm = isRecord(providers.llm) ? providers.llm : undefined;
    const tts = isRecord(providers.tts) ? providers.tts : undefined;
    if (stt) {
      language(stt.language, 'providers.stt.language', messages);
      if (runtimeSupport) requireSupportedProviderModel('stt', stt.provider, stt.model, messages);
      if (runtimeSupport && typeof stt.language === 'string' && !(supportedSttLanguages as readonly string[]).includes(stt.language)) messages.push('providers.stt.language must be one of: en, en-AU.');
    }
    if (stt && stt.settings !== undefined && !isRecord(stt.settings)) messages.push('providers.stt.settings must be an object.');
    if (stt && isRecord(stt.settings) && stt.settings.preemptive_tts !== undefined && stt.settings.preemptive_tts !== null && typeof stt.settings.preemptive_tts !== 'boolean') messages.push('providers.stt.settings.preemptive_tts must be boolean.');
    if (llm) {
      range(llm.temperature, 'providers.llm.temperature', 0, 2, messages);
      if (runtimeSupport) requireSupportedProviderModel('llm', llm.provider, llm.model, messages);
      if (runtimeSupport && isNumber(llm.temperature) && llm.temperature > supportedLlmMaxTemperature) messages.push(`providers.llm.temperature must be <= ${supportedLlmMaxTemperature}.`);
    }
    if (llm && llm.settings !== undefined && !isRecord(llm.settings)) messages.push('providers.llm.settings must be an object.');
    // The model itself is checked in validateProvider: required for save and publish, but a
    // saved agent without one must still load so the builder can fix it.
    if (tts && runtimeSupport) requireSupportedProviderModel('tts', tts.provider, tts.model, messages);
    if (tts && runtimeSupport && tts.provider === 'google' && typeof tts.voice === 'string') geminiVoiceMessage(tts.voice, messages);
    if (tts && tts.settings !== undefined && !isRecord(tts.settings)) messages.push('providers.tts.settings must be an object.');
    if (tts && isRecord(tts.settings)) validateTtsSettings(tts.settings, messages);
  }

  if (value.server !== undefined && value.server !== null) validateServerCallback(value.server, messages);
  if (value.context !== undefined && value.context !== null) validateContext(value.context, messages);
  if (value.triggers !== undefined && value.triggers !== null) validateTriggers(value.triggers, messages);

  if (value.tools !== undefined && !Array.isArray(value.tools)) messages.push('tools must be an array.');
  if (Array.isArray(value.tools)) {
    if (value.tools.length > 25) messages.push('tools supports at most 25 entries.');
    value.tools.forEach((tool, index) => validateTool(tool, `tools[${index}]`, messages));
  }

  const routing = requiredRecord(value.routing, 'routing', messages);
  if (routing) {
    rejectUnknownKeys(routing, ['strategy', 'targets'], 'routing', messages);
    literal(routing.strategy, 'single_agent', 'routing.strategy', messages);
    if (routing.targets !== undefined && !Array.isArray(routing.targets)) messages.push('routing.targets must be an array.');
    if (Array.isArray(routing.targets) && routing.targets.length > 0) messages.push('routing.targets must be empty for Agent Spec v1.');
  }

  const recording = requiredRecord(value.recording, 'recording', messages);
  if (recording) {
    rejectUnknownKeys(recording, ['record_audio', 'transcribe'], 'recording', messages);
    if (typeof recording.record_audio !== 'boolean') messages.push('recording.record_audio must be boolean.');
    if (typeof recording.transcribe !== 'boolean') messages.push('recording.transcribe must be boolean.');
  }

  if (value.limits !== undefined && !isRecord(value.limits)) messages.push('limits object is required.');
  const limits = isRecord(value.limits) ? value.limits : undefined;
  if (limits) {
    rejectUnknownKeys(limits, ['max_tool_calls_per_call', 'max_llm_tokens_per_turn'], 'limits', messages);
    if (limits.max_tool_calls_per_call !== undefined) range(limits.max_tool_calls_per_call, 'limits.max_tool_calls_per_call', 0, 50, messages);
    if (limits.max_llm_tokens_per_turn !== undefined) range(limits.max_llm_tokens_per_turn, 'limits.max_llm_tokens_per_turn', 128, 32000, messages);
  }

  return { valid: messages.length === 0, messages: messages.length ? messages : ['Spec-shaped draft is locally valid.'] };
}

function normalizeAgentSpec(value: unknown): CallableAgentSpecV1 {
  if (!isRecord(value)) throw new Error('Spec must be a JSON object.');
  const providers = value.providers as CallableAgentSpecV1['providers'];
  const routing = value.routing as Partial<CallableAgentSpecV1['routing']>;
  const limits = isRecord(value.limits) ? value.limits : {};
  return {
    schema_version: 'callable.agent.v1',
    name: value.name as string,
    description: value.description === undefined ? null : value.description as string | null,
    conversation: value.conversation as CallableAgentSpecV1['conversation'],
    providers: {
      stt: providers.stt,
      llm: providers.llm,
      tts: { ...providers.tts, settings: providers.tts.settings ?? {} },
    },
    // Preserve the exact callback object, including omitted/defaulted fields. Never
    // strip callbacks while editing an unrelated prompt or provider setting.
    ...(value.server === undefined ? {} : { server: value.server as CallableAgentSpecV1['server'] }),
    context: value.context === undefined ? null : value.context as CallableAgentSpecV1['context'],
    triggers: value.triggers === undefined ? null : value.triggers as CallableAgentSpecV1['triggers'],
    tools: Array.isArray(value.tools) ? value.tools.map((tool) => ({ ...(tool as CallableAgentSpecV1['tools'][number]), config: isRecord(tool) && tool.config !== undefined ? tool.config as Record<string, unknown> : {} })) : [],
    routing: { strategy: 'single_agent', targets: routing.targets ?? [] },
    recording: value.recording as CallableAgentSpecV1['recording'],
    limits: {
      max_tool_calls_per_call: (limits.max_tool_calls_per_call ?? 0) as number,
      max_llm_tokens_per_turn: (limits.max_llm_tokens_per_turn ?? 4096) as number,
    },
  };
}

function validateServerCallback(value: unknown, messages: string[]) {
  const server = requiredRecord(value, 'server', messages);
  if (!server) return;
  rejectUnknownKeys(server, ['enabled', 'url', 'messages', 'auth_mode', 'auth_header_name', 'signing_secret_ref', 'oidc_audience', 'timeout_ms', 'max_response_bytes'], 'server', messages);
  if (server.enabled !== undefined && typeof server.enabled !== 'boolean') messages.push('server.enabled must be boolean.');
  for (const field of ['url', 'oidc_audience'] as const) {
    if (server[field] !== undefined && server[field] !== null) boundedString(server[field], `server.${field}`, 0, 2048, messages);
  }
  if (server.auth_mode !== undefined) oneOf(server.auth_mode, ['hmac_sha256', 'header_secret', 'google_oidc'], 'server.auth_mode', messages);
  if (server.auth_header_name !== undefined && (typeof server.auth_header_name !== 'string' || !/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(server.auth_header_name))) messages.push('server.auth_header_name must be a valid header name.');
  if (server.signing_secret_ref !== undefined && server.signing_secret_ref !== null && (typeof server.signing_secret_ref !== 'string' || !/^credential:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(server.signing_secret_ref))) messages.push('server.signing_secret_ref must be a credential:<uuid> reference.');
  if (server.messages !== undefined) {
    if (!Array.isArray(server.messages) || server.messages.length > 10) messages.push('server.messages must be an array with at most 10 entries.');
    else {
      server.messages.forEach((message) => oneOf(message, ['status_update', 'end_of_call_report', 'tool_call', 'transfer_destination_request'], 'server.messages', messages));
      if (server.enabled && new Set(server.messages).size !== server.messages.length) messages.push('server.messages must be unique when callbacks are enabled.');
    }
  }
  for (const [field, min, max] of [['timeout_ms', 250, 10000], ['max_response_bytes', 256, 65536]] as const) {
    if (server[field] !== undefined) {
      range(server[field], `server.${field}`, min, max, messages);
      if (isNumber(server[field]) && !Number.isInteger(server[field])) messages.push(`server.${field} must be an integer.`);
    }
  }
  if (server.enabled === true) {
    if (server.url === undefined || server.url === null) messages.push('server.url is required when callbacks are enabled.');
    if (!Array.isArray(server.messages) || server.messages.length === 0) messages.push('server.messages is required when callbacks are enabled.');
    if (server.auth_mode === 'google_oidc') {
      if (server.oidc_audience === undefined || server.oidc_audience === null) messages.push('server.oidc_audience is required for google_oidc callbacks.');
    } else if (server.signing_secret_ref === undefined || server.signing_secret_ref === null) messages.push('server.signing_secret_ref is required when callbacks are enabled.');
  }
}

function requireSupportedProviderModel(purpose: 'stt' | 'llm' | 'tts', provider: unknown, model: unknown, messages: string[]) {
  if (typeof provider !== 'string') return;
  if (typeof model !== 'string') {
    messages.push(`providers.${purpose}.model is required for supported provider validation.`);
    return;
  }
  const modelsByProvider = supportedProviderModels[purpose] as Record<string, readonly string[] | undefined>;
  const allowedModels = modelsByProvider[provider];
  if (!allowedModels) {
    messages.push(`providers.${purpose}.provider is unsupported for publish: ${provider}.`);
    return;
  }
  if (!allowedModels.includes(model)) messages.push(`providers.${purpose}.model is unsupported for ${provider}: ${model}.`);
}

function validateProvider(value: unknown, path: string, providers: string[], allowedKeys: string[], messages: string[], requireModel = true) {
  const record = requiredRecord(value, path, messages);
  if (!record) return;
  rejectUnknownKeys(record, allowedKeys, path, messages);
  oneOf(record.provider, providers, `${path}.provider`, messages);
  const hasModel = record.model !== undefined && record.model !== null;
  if (allowedKeys.includes('model') && (requireModel || hasModel)) boundedString(record.model, `${path}.model`, 1, 120, messages);
  if (allowedKeys.includes('language')) boundedString(record.language, `${path}.language`, 2, 35, messages);
  if (allowedKeys.includes('voice')) boundedString(record.voice, `${path}.voice`, 1, 160, messages);
}

function validateTtsSettings(settings: Record<string, unknown>, messages: string[]) {
  rejectUnknownKeys(settings, ttsSettingsKeys, 'providers.tts.settings', messages);
  if (settings.language !== undefined && settings.language !== null) boundedString(settings.language, 'providers.tts.settings.language', 2, 35, messages);
  if (settings.speed !== undefined && settings.speed !== null) range(settings.speed, 'providers.tts.settings.speed', 0.6, 2.0, messages);
  if (settings.volume !== undefined && settings.volume !== null) range(settings.volume, 'providers.tts.settings.volume', 0, 2.0, messages);
  if (settings.sample_rate !== undefined && settings.sample_rate !== null && (typeof settings.sample_rate !== 'number' || ![8000, 16000, 24000, 44100, 48000].includes(settings.sample_rate))) messages.push('providers.tts.settings.sample_rate must be one of: 8000, 16000, 24000, 44100, 48000.');
  if (settings.streaming_latency !== undefined && settings.streaming_latency !== null) range(settings.streaming_latency, 'providers.tts.settings.streaming_latency', 0, 4, messages);
  if (settings.stability !== undefined && settings.stability !== null) range(settings.stability, 'providers.tts.settings.stability', 0, 1, messages);
  if (settings.similarity_boost !== undefined && settings.similarity_boost !== null) range(settings.similarity_boost, 'providers.tts.settings.similarity_boost', 0, 1, messages);
  if (settings.style !== undefined && settings.style !== null) range(settings.style, 'providers.tts.settings.style', 0, 1, messages);
  if (settings.use_speaker_boost !== undefined && settings.use_speaker_boost !== null && typeof settings.use_speaker_boost !== 'boolean') messages.push('providers.tts.settings.use_speaker_boost must be boolean.');
  if (settings.instructions !== undefined && settings.instructions !== null) boundedString(settings.instructions, 'providers.tts.settings.instructions', 0, 500, messages);
  if (settings.emotion !== undefined && settings.emotion !== null && typeof settings.emotion !== 'string' && !(Array.isArray(settings.emotion) && settings.emotion.every((item) => typeof item === 'string'))) messages.push('providers.tts.settings.emotion must be a string or array of strings.');
  if (settings.chunk_plan !== undefined && settings.chunk_plan !== null) {
    const chunkPlan = requiredRecord(settings.chunk_plan, 'providers.tts.settings.chunk_plan', messages);
    if (chunkPlan) {
      rejectUnknownKeys(chunkPlan, chunkPlanKeys, 'providers.tts.settings.chunk_plan', messages);
      if (typeof chunkPlan.enabled !== 'boolean') messages.push('providers.tts.settings.chunk_plan.enabled must be boolean.');
      range(chunkPlan.minCharacters, 'providers.tts.settings.chunk_plan.minCharacters', 1, 500, messages);
      if (!Array.isArray(chunkPlan.punctuationBoundaries) || !chunkPlan.punctuationBoundaries.every((item) => typeof item === 'string' && item.length >= 1 && item.length <= 8)) messages.push('providers.tts.settings.chunk_plan.punctuationBoundaries must be an array of 1-8 character strings.');
      else if (chunkPlan.punctuationBoundaries.length > 16) messages.push('providers.tts.settings.chunk_plan.punctuationBoundaries must contain at most 16 items.');
    }
  }
  if (settings.first_clause_buffer !== undefined && settings.first_clause_buffer !== null) {
    const buffer = requiredRecord(settings.first_clause_buffer, 'providers.tts.settings.first_clause_buffer', messages);
    if (buffer) {
      rejectUnknownKeys(buffer, firstClauseBufferKeys, 'providers.tts.settings.first_clause_buffer', messages);
      if (typeof buffer.enabled !== 'boolean') messages.push('providers.tts.settings.first_clause_buffer.enabled must be boolean.');
      range(buffer.min_chars, 'providers.tts.settings.first_clause_buffer.min_chars', 8, 120, messages);
      range(buffer.context_chars, 'providers.tts.settings.first_clause_buffer.context_chars', 0, 200, messages);
    }
  }
}

function validateTool(value: unknown, path: string, messages: string[]) {
  const tool = requiredRecord(value, path, messages);
  if (!tool) return;
  rejectUnknownKeys(tool, toolKeys, path, messages);
  oneOf(tool.kind, ['disposition_call', 'live_transfer', 'end_call', 'capture_fields', 'call_summary', 'mcp_connector'], `${path}.kind`, messages);
  boundedString(tool.name, `${path}.name`, 1, 120, messages);
  if (tool.kind === 'mcp_connector') boundedString(tool.connector_id, `${path}.connector_id`, 1, 160, messages);
  if (tool.kind !== 'mcp_connector' && tool.connector_id !== undefined && tool.connector_id !== null) messages.push(`${path}.connector_id is only valid for mcp_connector tools.`);
  if (tool.kind === 'disposition_call') validateDispositionToolConfig(tool.config, `${path}.config`, messages);
  if (tool.kind === 'capture_fields') validateCaptureFieldsToolConfig(tool.config, `${path}.config`, messages);
  if (tool.kind === 'end_call') validateEndCallToolConfig(tool.config, `${path}.config`, messages);
  if (tool.description !== undefined && tool.description !== null) boundedString(tool.description, `${path}.description`, 0, 1000, messages);
  if (tool.input_schema !== undefined && tool.input_schema !== null) validateSchemaObject(tool.input_schema, `${path}.input_schema`, messages);
  if (tool.output_schema !== undefined && tool.output_schema !== null) validateSchemaObject(tool.output_schema, `${path}.output_schema`, messages);
  if (tool.required_context !== undefined && tool.required_context !== null) validateStringArray(tool.required_context, `${path}.required_context`, 50, messages);
  if (tool.required_credentials !== undefined && tool.required_credentials !== null && !Array.isArray(tool.required_credentials)) messages.push(`${path}.required_credentials must be an array.`);
  if (tool.timeout_ms !== undefined && tool.timeout_ms !== null) range(tool.timeout_ms, `${path}.timeout_ms`, 100, 30000, messages);
  if (tool.max_attempts !== undefined && tool.max_attempts !== null) range(tool.max_attempts, `${path}.max_attempts`, 1, 5, messages);
  if (tool.audit !== undefined && tool.audit !== null && !isRecord(tool.audit)) messages.push(`${path}.audit must be an object.`);
  if (tool.config !== undefined && !isRecord(tool.config)) messages.push(`${path}.config must be an object.`);
}

function validateEndCallToolConfig(value: unknown, path: string, messages: string[]) {
  const config = requiredRecord(value, path, messages);
  if (!config) return;
  rejectUnknownKeys(config, endCallConfigKeys, path, messages);
  const mode = config.final_message_mode;
  if (mode !== undefined && mode !== null) oneOf(mode, endCallFinalMessageModes, `${path}.final_message_mode`, messages);
  const finalMessage = config.final_message;
  if (finalMessage !== undefined && finalMessage !== null) boundedString(finalMessage, `${path}.final_message`, 1, 500, messages);
  if (mode === 'scripted' && (typeof finalMessage !== 'string' || !finalMessage.trim())) messages.push(`${path}.final_message is required when final_message_mode is scripted.`);
}

function validateCaptureFieldsToolConfig(value: unknown, path: string, messages: string[]) {
  const config = requiredRecord(value, path, messages);
  if (!config) return;
  const fields = config.fields;
  if (!Array.isArray(fields) || fields.length === 0) {
    messages.push(`${path}.fields is required for capture_fields.`);
    return;
  }
  if (fields.length > 50) messages.push(`${path}.fields supports at most 50 entries.`);
  const keys = new Set<string>();
  fields.forEach((item, index) => {
    const field = requiredRecord(item, `${path}.fields[${index}]`, messages);
    if (!field) return;
    rejectUnknownKeys(field, captureFieldKeys, `${path}.fields[${index}]`, messages);
    if (typeof field.key !== 'string' || !/^[a-z][a-z0-9_]{1,79}$/.test(field.key)) messages.push(`${path}.fields[${index}].key must be a stable snake_case key.`);
    if (typeof field.key === 'string') {
      if (keys.has(field.key)) messages.push(`${path}.fields keys must be unique.`);
      keys.add(field.key);
    }
    boundedString(field.label, `${path}.fields[${index}].label`, 1, 120, messages);
    oneOf(field.type, captureFieldTypes, `${path}.fields[${index}].type`, messages);
    if (field.description !== undefined && field.description !== null) boundedString(field.description, `${path}.fields[${index}].description`, 0, 500, messages);
    if (field.sensitive !== undefined && typeof field.sensitive !== 'boolean') messages.push(`${path}.fields[${index}].sensitive must be boolean.`);
  });
}

function validateDispositionToolConfig(value: unknown, path: string, messages: string[]) {
  const config = requiredRecord(value, path, messages);
  if (!config) return;
  const dispositions = config.available_dispositions;
  if (!Array.isArray(dispositions) || dispositions.length === 0) {
    messages.push(`${path}.available_dispositions is required for disposition_call.`);
    return;
  }
  if (dispositions.length > 50) messages.push(`${path}.available_dispositions supports at most 50 entries.`);
  const keys = new Set<string>();
  dispositions.forEach((item, index) => {
    const option = requiredRecord(item, `${path}.available_dispositions[${index}]`, messages);
    if (!option) return;
    if (typeof option.key !== 'string' || !/^[a-z][a-z0-9_]{1,79}$/.test(option.key)) messages.push(`${path}.available_dispositions[${index}].key must be a stable snake_case key.`);
    if (typeof option.key === 'string') {
      if (keys.has(option.key)) messages.push(`${path}.available_dispositions keys must be unique.`);
      keys.add(option.key);
    }
    boundedString(option.label, `${path}.available_dispositions[${index}].label`, 1, 120, messages);
    if (option.description !== undefined && option.description !== null) boundedString(option.description, `${path}.available_dispositions[${index}].description`, 0, 500, messages);
  });
}

function validateContext(value: unknown, messages: string[]) {
  const context = requiredRecord(value, 'context', messages);
  if (!context) return;
  rejectUnknownKeys(context, contextKeys, 'context', messages);
  if (context.inputs !== undefined) validateArray(context.inputs, 'context.inputs', 100, messages);
  if (context.collections !== undefined) validateArray(context.collections, 'context.collections', 100, messages);
  if (context.writebacks !== undefined) validateArray(context.writebacks, 'context.writebacks', 100, messages);
  if (context.rendering !== undefined && !isRecord(context.rendering)) messages.push('context.rendering must be an object.');
  if (context.sample_context !== undefined && !isRecord(context.sample_context)) messages.push('context.sample_context must be an object.');
}

function validateTriggers(value: unknown, messages: string[]) {
  if (!Array.isArray(value)) {
    messages.push('triggers must be an array.');
    return;
  }
  if (value.length > 20) messages.push('triggers supports at most 20 entries.');
  value.forEach((trigger, index) => {
    const path = `triggers[${index}]`;
    const record = requiredRecord(trigger, path, messages);
    if (!record) return;
    rejectUnknownKeys(record, triggerKeys, path, messages);
    oneOf(record.kind, ['inbound_call', 'test_call'], `${path}.kind`, messages);
    boundedString(record.name, `${path}.name`, 1, 120, messages);
    if (record.description !== undefined && record.description !== null) boundedString(record.description, `${path}.description`, 0, 1000, messages);
    if (record.context !== undefined && !isRecord(record.context)) messages.push(`${path}.context must be an object.`);
  });
}

function validateSchemaObject(value: unknown, path: string, messages: string[]) {
  const record = requiredRecord(value, path, messages);
  if (!record) return;
  rejectUnknownKeys(record, ['schema'], path, messages);
  if (!isRecord(record.schema)) messages.push(`${path}.schema must be an object.`);
}

function validateArray(value: unknown, path: string, maxLength: number, messages: string[]) {
  if (!Array.isArray(value)) {
    messages.push(`${path} must be an array.`);
    return;
  }
  if (value.length > maxLength) messages.push(`${path} supports at most ${maxLength} entries.`);
}

function validateStringArray(value: unknown, path: string, maxLength: number, messages: string[]) {
  validateArray(value, path, maxLength, messages);
  if (Array.isArray(value)) {
    value.forEach((item, index) => boundedString(item, `${path}[${index}]`, 1, 160, messages));
  }
}

function rejectUnknownKeys(value: Record<string, unknown>, allowed: string[], path: string, messages: string[]) {
  const allowedSet = new Set(allowed);
  for (const key of Object.keys(value)) {
    if (!allowedSet.has(key)) messages.push(`Unknown field: ${path ? `${path}.` : ''}${key}`);
  }
}

function requiredRecord(value: unknown, path: string, messages: string[]): Record<string, unknown> | undefined {
  if (!isRecord(value)) {
    messages.push(`${path} object is required.`);
    return undefined;
  }
  return value;
}

function literal(value: unknown, expected: string, path: string, messages: string[]) {
  if (value !== expected) messages.push(`${path} must be ${expected}.`);
}

function oneOf(value: unknown, values: string[], path: string, messages: string[]) {
  if (typeof value !== 'string' || !values.includes(value)) messages.push(`${path} must be one of: ${values.join(', ')}.`);
}

function boundedString(value: unknown, path: string, min: number, max: number, messages: string[]) {
  if (typeof value !== 'string' || value.length < min || value.length > max) messages.push(`${path} must be a string between ${min} and ${max} characters.`);
}

function language(value: unknown, path: string, messages: string[]) {
  if (typeof value !== 'string' || !/^[a-z]{2,3}(-[A-Z]{2})?$/.test(value)) messages.push(`${path} must match BCP-47 style language, e.g. en-AU.`);
}

function range(value: unknown, path: string, min: number, max: number, messages: string[]) {
  if (!isNumber(value) || value < min || value > max) messages.push(`${path} must be between ${min} and ${max}.`);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
